// File Metadata:
// Filename: DrawerNav.js
// Version: 1.00
// Created: 2022-10-14 23:13:34
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import useAuth from '../hooks/useAuth'

import { InboxIcon, MailIcon } from '@mui/icons-material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faUserGear, faUserPlus } from '@fortawesome/free-solid-svg-icons'
import {
	SwipeableDrawer,
	ListSubheader,
	Box,
	Collapse,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText
} from '@mui/material'

import {
    People as PeopleIcon,
    AccessTime as TimeIcon,
    HealthAndSafety as HealthIcon,
    LocalShipping as ShippingIcon,
    Assignment as TaskIcon,
    Group as TeamIcon,
    Warehouse as WarehouseIcon,
    ExpandLess, ExpandMore,
    ManageAccounts as ManageAccountsIcon,
    Assessment as AssessmentIcon,
    Store as VendorIcon,
    AddBusiness as VendorAddIcon
} from '@mui/icons-material'

import GroupsIcon from '@mui/icons-material/Groups'
import EngineeringIcon from '@mui/icons-material/Engineering'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'

const DrawerNav = ({ open, handleDrawerClose }) => {
	const { isVoorman, isAdmin, isManager } = useAuth()
	console.log('DrawerNav', isVoorman, isAdmin, isManager)
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [state, setState] = useState({
		top: false,
		left: false,
		bottom: false,
		right: false
	})

    const [subOpen, setSubOpen] = useState(false)

    const handleClick = (event) => {
        event.stopPropagation()
        setSubOpen(!subOpen)
    }

	const [menuExpanded, setMenuExpanded] = useState({
        workforce: false,
        operations: false,
        admin: false
    })

	const [subMenuOpen, setSubMenuOpen] = useState({})

    const handleSubMenu = (itemLabel) => {
        setSubMenuOpen(prev => ({
            ...prev,
            [itemLabel]: !prev[itemLabel]
        }))
    }

    const isSubMenuOpen = (itemLabel) => {
        return subMenuOpen[itemLabel] || false
    }

	const toggleDrawer = (anchor, open) => event => {
		if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
			return
		}

		setState({ ...state, [anchor]: open })
	}

	

	const menuItems = [
        // Operations Group
        {
            group: 'operations',
            label: 'Operations',
            icon: <WarehouseIcon />,
            requiresRole: ['Employee+'],
            items: [
                {
                    label: 'Tasks',
                    icon: <TaskIcon />,
                    path: '/dash/tasks',
                    requiresRole: ['Employee+']
                },
                {
                    label: 'Shipments',
                    icon: <ShippingIcon />,
                    path: '/dash/shipments',
                    requiresRole: ['Employee+']
                }
            ]
        },
        
        // Workforce Management Group
        {
            group: 'workforce',
            label: 'Workforce',
            icon: <PeopleIcon />,
            requiresRole: ['Voorman+'],
            items: [
                {
                    label: 'Team Schedule',
                    icon: <TimeIcon />,
                    path: '/dash/schedule',
                    requiresRole: ['Voorman+']
                },
                {
                    label: 'Crew Management',
                    icon: <TeamIcon />,
                    path: '/dash/crews',
                    requiresRole: ['Coordinator+']
                },
                {
                    label: 'Health & Safety',
                    icon: <HealthIcon />,
                    path: '/dash/health',
                    requiresRole: ['Voorman+'],
                    subItems: [
                        {
                            label: 'Medical Checkups',
                            path: '/dash/health/checkups',
                            requiresRole: ['Voorman+']
                        },
                        {
                            label: 'Sick Leave',
                            path: '/dash/health/sickleave',
                            requiresRole: ['Voorman+']
                        },
                        {
                            label: 'Competencies',
                            path: '/dash/health/competencies',
                            requiresRole: ['Coordinator+']
                        }
                    ]
                }
            ]
        }
    ]

	const renderMenuItem = (item) => {
        if (item.subItems) {
            return (
                <div key={item.label}>
                    <ListItemButton 
                        onClick={() => handleSubMenu(item.label)}
                        sx={{ pl: item.level * 2 }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                        {isSubMenuOpen(item.label) ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={isSubMenuOpen(item.label)} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.subItems.map(subItem => (
                                <ListItemButton
                                    key={subItem.label}
                                    component={Link}
                                    to={subItem.path}
                                    sx={{ pl: (item.level + 1) * 2 }}
                                >
                                    <ListItemText primary={subItem.label} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                </div>
            )
        }

        return (
            <ListItemButton
                key={item.label}
                component={Link}
                to={item.path}
                sx={{ pl: item.level * 2 }}
            >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
            </ListItemButton>
        )
    }

    const adminItems = isAdmin && (
        <>
            <ListItemButton
                component={Link}
                to="/dash/users"
                selected={pathname.includes('/users')}
            >
                <ListItemIcon><PeopleIcon /></ListItemIcon>
                <ListItemText primary="Users" />
            </ListItemButton>

            <ListItemButton
                component={Link}
                to="/dash/users/new"
                selected={pathname.includes('/users/new')}
            >
				<ListItemIcon><FontAwesomeIcon icon={faUserPlus} /></ListItemIcon>
				<ListItemText primary='Add new User' />
			</ListItemButton>

            <ListItemButton
                component={Link}
                to="/dash/user-reports"
                selected={pathname.includes('/user-reports')}
            >
                <ListItemIcon><AssessmentIcon /></ListItemIcon>
                <ListItemText primary="Reports" />
            </ListItemButton>
            <ListItemButton onClick={handleClick}>
                <ListItemIcon><GroupsIcon /></ListItemIcon>
                <ListItemText primary="Crews" />
                {subOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={subOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemButton 
                        component={Link} 
                        to="/dash/crews"
                        selected={pathname.includes('/crews')}
                    >
                        <ListItemIcon><GroupsIcon /></ListItemIcon>
                        <ListItemText primary="Crews" />
                    </ListItemButton>

                    <ListItemButton 
                        component={Link} 
                        to="/dash/crews/schedule"
                        selected={pathname.includes('/crews/schedule')}
                    >
                        <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
                        <ListItemText primary="Crew Schedule" />
                    </ListItemButton>

                    <ListItemButton 
                        component={Link} 
                        to="/dash/crews/rotation"
                        selected={pathname.includes('/crews/rotation')}
                    >
                        <ListItemIcon><EngineeringIcon /></ListItemIcon>
                        <ListItemText primary="Crew Rotation" />
                    </ListItemButton>
                </List>
            </Collapse>
            <ListItemButton
                component={Link}
                to="/dash/vendors"
                selected={pathname.includes('/vendors')}
            >   
                <ListItemIcon><VendorIcon /></ListItemIcon>
                <ListItemText primary='Vendor list' />
            </ListItemButton>

            <ListItemButton
                component={Link}
                to="/dash/vendors/new"
                selected={pathname.includes('/vendors/new')}
            >
                <ListItemIcon><VendorAddIcon /></ListItemIcon>
                <ListItemText primary='Add new vendor' />
            </ListItemButton>
        </>
    )

	const list = () => (
		<Box
			sx={{ width: 250, boxShadow: 1 }}
			role='presentation'
			onClick={toggleDrawer('left', false)}
			onKeyDown={toggleDrawer('left', false)}
		>
			{
				<List dense={true} sx={{ width: '100%', maxWidth: 360 }} component='nav' color='primary'>
					<ListSubheader>SubHeader</ListSubheader>					
					<ListItem disableGutters={true}>
						<ListItemButton>
							<Link to='dash/shipments'>Shipments</Link>
						</ListItemButton>
					</ListItem>
					<ListItem disableGutters={true}>
						<ListItemButton>
							<Link to='dash/receptiongrid'>Receptions Grid</Link>
						</ListItemButton>
					</ListItem>
				</List>
			}
			<Divider />
            {adminItems}
            <Divider />

		</Box>
	)

	return (
		<div>
			<button className='icon-button' onClick={toggleDrawer('left', true)}>
				{<FontAwesomeIcon icon={faBars} />}
			</button>
			<SwipeableDrawer
				anchor={'left'}
				open={state['left']}
				onClose={toggleDrawer('left', false)}
				onOpen={toggleDrawer('left', true)}
			>
				{list('left')}
			</SwipeableDrawer>
		</div>
	)
}

export default DrawerNav
